import {
  DocumentFragment,
  Item,
  Node,
  Writer,
  DowncastWriter,
  Element,
  Range,
  ViewDocumentFragment,
  ViewElement,
  ViewNode,
  ViewRange,
} from "ckeditor5";

import ListWalker from "../_nonpublic/list/listwalker";
import { isListItemBlock } from "../_nonpublic/list/model";

export const replaceModelItem = (
  original: Node,
  replacements: Item | Item[],
  writer: Writer,
) => {
  if (!Array.isArray(replacements)) {
    replacements = [replacements];
  }

  let position = writer.createPositionBefore(original);
  writer.remove(original);
  for (const replacement of replacements) {
    writer.insert(replacement, position);
    position = position.getShiftedBy(replacement.offsetSize);
  }
};

export const collectAncestors = (node: Node) => {
  if (isListItemBlock(node)) {
    return walkList(node, "backward");
  }

  const ancestors: Element[] = [];

  let current = node.parent;

  while (current != null) {
    if (current?.is("model:element")) {
      ancestors.push(current);
    }
    current = current.parent;
  }

  return ancestors;
};

export const collectDescendents = (node: Node) => {
  if (!node.is("model:element")) return [];

  if (isListItemBlock(node)) {
    return walkList(node, "forward");
  }

  const descendents: Element[] = [];

  for (const child of node.getChildren()) {
    if (!child.is("model:element")) continue;
    descendents.push(child, ...collectDescendents(child));
  }

  return descendents;
};

export const walkList = (
  element: Element,
  direction: "forward" | "backward",
) => {
  return Array.from(
    new ListWalker(element, {
      direction,
      includeSelf: false,
      lowerIndent: direction === "backward",
      higherIndent: direction === "forward",
    }),
  );
};

export const removeClassesMatching = (
  pattern: RegExp,
  element: ViewElement,
  writer: DowncastWriter,
) => {
  const classNames = Array.from(element.getClassNames()).filter(
    (c) => !!pattern.test(c),
  );
  for (const className in classNames) {
    writer.removeClass(className, element);
  }
  return classNames;
};

export const getModelText = (node: Node | Range | Item | undefined | null) => {
  if (node == null) return undefined;

  if (node instanceof Range) {
    [node] = node.getItems() as IterableIterator<Node | undefined>;
  }

  if (node?.is("$text") || node?.is("$textProxy")) {
    return node.data;
  }
  if (!node?.is("element")) return undefined;

  const [child, other] = node.getChildren() as IterableIterator<
    Node | undefined
  >;
  if (other != null) {
    //
    // Node has more than one child; assume this is not a text situation
    //
    return undefined;
  }

  if (!child?.is("$text")) return undefined;

  return child.data;
};

export const visitModel = (
  node: DocumentFragment | Node | null | undefined,
  callback: (n: Node, opts: { stopDescending: () => void }) => void,
) => {
  if (node == null) return;

  let stopped = false;

  if (node.is("node")) {
    callback(node, {
      stopDescending: () => {
        stopped = true;
      },
    });
  }

  if (stopped) return;

  if (node.is("documentFragment") || node.is("element")) {
    for (const child of node.getChildren()) {
      visitModel(child, callback);
    }
  }
};

export const collectModel = (
  node: DocumentFragment | Node | null | undefined,
  predicate: (node: Node) => boolean,
) => {
  const results: Node[] = [];

  visitModel(node, (n) => {
    if (predicate(n)) results.push(n);
  });
  return results;
};

export const collectModelElements = (
  node: DocumentFragment | Node | null | undefined,
  elementName: string,
  predicate?: (element: Element) => boolean,
): Element[] =>
  collectModel(
    node,
    (n) =>
      n.is("element") && n.name === elementName && predicate?.(n) !== false,
  ) as Element[];

export const getViewText = (node: ViewNode) => {
  if (node.is("$text")) {
    return node.data;
  }
  if (!node.is("element")) return undefined;

  const [child, other] = node.getChildren() as IterableIterator<
    ViewNode | undefined
  >;
  if (other != null) {
    //
    // Node has more than one child; assume this is not a text situation
    //
    return undefined;
  }

  if (!child?.is("$text")) return undefined;

  return child.data;
};

export const visitView = (
  node: ViewDocumentFragment | ViewNode | null | undefined,
  callback: (n: ViewNode, opts: { stopDescending: () => void }) => void,
) => {
  if (node == null) return;

  let stopped = false;

  if (node.is("node")) {
    callback(node, {
      stopDescending: () => {
        stopped = true;
      },
    });
  }

  if (stopped) return;

  if (node.is("documentFragment") || node.is("element")) {
    for (const child of node.getChildren()) {
      visitView(child, callback);
    }
  }
};

export const collectView = (
  node: ViewDocumentFragment | ViewNode | null | undefined,
  predicate: (node: ViewNode) => boolean,
) => {
  const results: ViewNode[] = [];
  visitView(node, (n) => {
    if (predicate(n)) results.push(n);
  });
  return results;
};

export const collectViewElements = (
  node: ViewDocumentFragment | ViewNode | null | undefined,
  elementName: string,
  predicate?: (element: ViewElement) => boolean,
): ViewElement[] =>
  collectView(
    node,
    (n) =>
      n.is("element") && n.name === elementName && predicate?.(n) !== false,
  ) as ViewElement[];

export const findCommonAncestorElement = (
  range: ViewRange | null | undefined,
  predicate: (e: ViewElement) => boolean,
) => {
  const commonAncestor = range?.getCommonAncestor();

  if (!commonAncestor?.is("node")) {
    return null;
  }

  if (commonAncestor?.is("element") && predicate(commonAncestor)) {
    return commonAncestor;
  }

  return (
    commonAncestor
      .getAncestors()
      .find((a): a is ViewElement => a.is("element") && predicate(a)) ?? null
  );
};
